import React, { useEffect, useState } from "react";
import { Seo } from "@/components/Seo";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { api } from "@/lib/api";
import { TIERS, currentTier, progressToNext, earnedThemed, earnedReferral } from "@/lib/badges";
import { BackLink } from "@/components/BackLink";
import { ShareButton } from "@/components/ShareButton";
import { useAuth } from "@/context/AuthContext";
import { Award, Trophy, FileText } from "lucide-react";

export default function Badges() {
  const { user } = useAuth();
  const [stats, setStats] = useState({ total_hours: 0, logs_count: 0, orgs_count: 0 });

  useEffect(() => { api.myStats().then(setStats).catch(() => {}); }, []);

  const tier = currentTier(stats.total_hours);
  const prog = progressToNext(stats.total_hours);
  const themed = earnedThemed(stats);
  const referral = earnedReferral(stats);

  return (
    <div className="max-w-6xl mx-auto px-6 py-10">
      <Seo title="Badges" />
      <BackLink />
      <div className="text-xs uppercase tracking-[0.2em] text-forest/70 font-bold">Recognition</div>
      <h1 className="font-display text-4xl text-forest mt-2 tracking-tight">Your badges</h1>

      {/* Tiered */}
      <div className="mt-8 bg-white border border-neutral-200 rounded-xl p-8">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.18em] text-forest/70 font-bold">Tier</div>
            <div className="font-display text-3xl text-forest mt-1">{tier ? tier.label : "Not yet ranked"}</div>
            <div className="text-sm text-neutral-600 mt-1">{stats.total_hours} approved hours</div>
          </div>
          {tier && (
            <div className="flex items-center gap-2">
              {user && (
                <ShareButton
                  testId="share-badge-btn"
                  imageUrl={api.ogBadgeUrl(user.user_id)}
                  shareUrl={`${window.location.origin}/u/${user.user_id}`}
                  title="My CivicHearth badge"
                  text={`I've volunteered ${stats.total_hours} hours on CivicHearth 💚 — ${tier.label} tier!`}
                  filename="civichearth-badge.png"
                  label="Share badge"
                />
              )}
              <Link to={`/certificate/${tier.id}`}>
                <Button className="rounded-full bg-forest hover:bg-forest-hover h-11 px-6">
                  <FileText className="w-4 h-4 mr-2" /> Download certificate
                </Button>
              </Link>
            </div>
          )}
        </div>
        <Progress value={prog.pct} className="mt-5 h-2" />
        {prog.next && <div className="text-xs text-neutral-500 mt-2">{prog.next.threshold - stats.total_hours}h to {prog.next.label}</div>}

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
          {TIERS.map(t => {
            const earned = stats.total_hours >= t.threshold;
            return (
              <div key={t.id} className={`rounded-xl p-5 border ${earned ? "border-forest/30 bg-forest/5" : "border-neutral-200 bg-neutral-50 opacity-70"}`}>
                <Trophy className="w-7 h-7" style={{ color: t.color }} />
                <div className="font-display text-lg text-forest mt-3">{t.label}</div>
                <div className="text-xs uppercase tracking-[0.18em] text-neutral-500 mt-1">{t.threshold}h</div>
                {earned && <div className="text-xs text-forest font-medium mt-2">Earned</div>}
              </div>
            );
          })}
        </div>
      </div>

      {/* Themed */}
      <h2 className="font-display text-2xl text-forest mt-12 tracking-tight">Achievements</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
        {themed.map(b => (
          <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-xl p-6 border ${b.earned ? "border-terracotta/30 bg-white" : "border-neutral-200 bg-neutral-50 opacity-60"}`}>
            <Award className={`w-7 h-7 ${b.earned ? "text-terracotta" : "text-neutral-400"}`} />
            <div className="font-display text-lg text-forest mt-3">{b.label}</div>
            <div className="text-sm text-neutral-600 mt-1">{b.desc}</div>
            <div className="text-xs uppercase tracking-[0.18em] mt-3 font-bold" style={{ color: b.earned ? "#D36135" : "#9CA3AF" }}>
              {b.earned ? "Earned" : "Locked"}
            </div>
          </div>
        ))}
      </div>

      {/* Referral */}
      <h2 className="font-display text-2xl text-forest mt-12 tracking-tight">Invites</h2>
      <p className="text-sm text-neutral-600 mt-1">
        Refer friends via your <Link to="/dashboard" className="underline text-forest">dashboard invite card</Link>. Badges unlock when they complete onboarding.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-5">
        {referral.map(b => (
          <div key={b.id} data-testid={`badge-${b.id}`} className={`rounded-xl p-6 border ${b.earned ? "border-forest/30 bg-white" : "border-neutral-200 bg-neutral-50 opacity-60"}`}>
            <Award className={`w-7 h-7 ${b.earned ? "text-forest" : "text-neutral-400"}`} />
            <div className="font-display text-lg text-forest mt-3">{b.label}</div>
            <div className="text-sm text-neutral-600 mt-1">{b.desc}</div>
            <div className="text-xs uppercase tracking-[0.18em] mt-3 font-bold" style={{ color: b.earned ? "#1A4331" : "#9CA3AF" }}>
              {b.earned ? "Earned" : `${Math.max(0, b.threshold - (stats.referral_count || 0))} to go`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
